import { useCallback, useRef } from 'react';
import type { UseFormSetValue, FieldValues } from 'react-hook-form';
import type { SelectOption } from '../composites/Select';
import type { Effect } from './reactions';

/**
 * Handles outbound effects — when THIS field's value changes, applies clear/set
 * operations to sibling fields via setValue.
 *
 * Returns a wrapped onChange handler that the adapter should call instead of
 * (or in addition to) field.onChange. The handler:
 * 1. Evaluates each effect's `when` guard (if provided)
 * 2. Clears specified sibling fields
 * 3. Sets specified sibling fields using the compute functions
 *
 * Effects don't cascade: setValue calls made here use `{ shouldValidate: false }`
 * to avoid triggering re-validation cascades, and adapters should NOT re-fire
 * effects when their value is set programmatically by another effect.
 */
export function useEffects(
  effects: Effect[] | undefined,
  setValue: UseFormSetValue<FieldValues> | undefined,
) {
  const prevValueRef = useRef<unknown>(undefined);

  const runEffects = useCallback(
    (newValue: unknown, option?: SelectOption & Record<string, unknown>) => {
      if (!effects || effects.length === 0 || !setValue) return;

      const prevValue = prevValueRef.current;
      prevValueRef.current = newValue;

      for (const effect of effects) {
        if (effect.when && !effect.when(newValue, prevValue)) {
          continue;
        }

        if (effect.clear) {
          for (const fieldName of effect.clear) {
            setValue(fieldName, '', { shouldValidate: false, shouldDirty: true });
          }
        }

        if (effect.set) {
          for (const [fieldName, compute] of Object.entries(effect.set)) {
            const value = compute(newValue, option);
            setValue(fieldName, value, { shouldValidate: false, shouldDirty: true });
          }
        }
      }
    },
    [effects, setValue],
  );

  return { runEffects };
}
